<template>
    <div>
        <FriendTabs />

        <h1 class="mb-0">
            {{ title }}
        </h1>
        <h4 v-if="totalPages">
            Page {{ currentPage }} of {{ totalPages }}
        </h4>

        <PageLoadingSpinner v-if="loading"></PageLoadingSpinner>

        <PageLoadingError v-else-if="errorMessage"
                          :error="errorMessage"
                          :code="errorCode"></PageLoadingError>

        <div v-else-if="!hasFriends"
             class="alert alert-info">
            <template v-if="currentList === 'deleted-me'">
                <h3>Nobody has deleted you since you started using Who Deleted Me.</h3>
                <p>Check back later to see if anybody goes missing.</p>
            </template>
            <template v-else-if="currentList === 'i-deleted'">
                <h3>You haven't marked that you deleted anybody.</h3>
                <p>
                    When somebody appears on Who Deleted Me, click the "Move To I Deleted" button to move them to this
                    tab.
                </p>
            </template>
            <template v-else-if="currentList === 'deactivated'">
                <h3>Nobody has deactivated their account since you started using Who Deleted Me.</h3>
                <p>
                    When a friend deactivates their account we'll let you know here.
                </p>
            </template>
            <template v-else-if="currentList === 'current'">
                <h3>We couldn't find any friends!</h3>
                <p>You probably have some, but we couldn't see any. Please contact us for help.</p>
            </template>
        </div>

        <template v-else>
            <Friends :friends="friends"></Friends>

            <Paginator :total-pages="totalPages"
                       :current-page="currentPage"
                       :url="'/friends/'+currentList+'/'" />
        </template>

        <div v-if="!loading"
             class="text-center mt-1">
            <a href="/sync"
               class="btn btn-info"><i class="glyphicon glyphicon-refresh"></i> Check Friends Again</a>
        </div>
    </div>
</template>

<script>
import Friends from '@/app/pages/friends/Friends';
import FriendTabs from '@/app/pages/friends/FriendTabs';
import PageLoadingError from '@/app/components/PageLoadingError';
import PageLoadingSpinner from '@/app/components/PageLoadingSpinner';
import Paginator from '@/app/components/Paginator';
import { mapState } from 'vuex';
import { sendBackendRequest } from '@/app/lib/Messaging/requests';
import FriendStatus from 'wdm-common/Friends/FriendStatus';
import RequestType from 'wdm-common/Messaging/RequestType';

export default {
    components: {
        Paginator,
        PageLoadingSpinner,
        PageLoadingError,
        Friends,
        FriendTabs,
    },

    data() {
        return {
            /**
             * @type {Number}
             */
            friendsPerPage: 15,

            /**
             * @type {Array}
             */
            friends: null,

            /**
             * @type {Number|null}
             */
            totalFriends: null,

            /**
             * @type {boolean}
             */
            loading: true,

            /**
             * @type {String|null}
             */
            errorMessage: null,

            /**
             * @type {String|null}
             */
            errorCode: null,
        };
    },

    computed: {
        ...mapState(['user']),

        currentList() {
            return this.$route.params.list;
        },

        currentPage() {
            return parseInt(this.$route.params.page || 1);
        },

        currentOffset() {
            return (this.currentPage - 1) * this.friendsPerPage;
        },

        totalPages() {
            if (this.totalFriends !== null) {
                return Math.ceil(this.totalFriends / this.friendsPerPage);
            }

            return null;
        },

        title() {
            switch (this.currentList) {
                case 'deleted-me':
                    return 'Friends That Deleted You';

                case 'i-deleted':
                    return 'Friends You Deleted';

                case 'deactivated':
                    return 'Friends That Deactivated';

                case 'current':
                    return 'Your Current Friends';
            }

            return '';
        },

        friendStatus() {
            switch (this.currentList) {
                case 'deleted-me':
                    return FriendStatus.DELETED;

                case 'i-deleted':
                    return FriendStatus.DELETED_BY_USER;

                case 'deactivated':
                    return FriendStatus.DEACTIVATED;

                case 'current':
                    return FriendStatus.CURRENT;
            }

            return null;
        },

        hasFriends() {
            return this.friends && this.friends.length > 0;
        },

        hasNoFriends() {
            return !this.hasFriends;
        },
    },

    created() {
        this.loadFriends();
    },

    methods: {
        async loadFriends() {
            this.loading = true;

            sendBackendRequest(
                RequestType.GET_FRIENDS,
                {
                    status: this.friendStatus,
                    offset: this.currentOffset,
                    limit: this.friendsPerPage,
                },
            )
                .then((result) => {
                    this.friends = result.friends;
                    this.totalFriends = result.total;
                })
                .catch((e) => {
                    console.error(e);
                    this.errorMessage = e.errorMessage || e.message;
                    this.errorCode = e.errorCode;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
    },
};
</script>
